/** Confirmation dialog for the built-in (`action`-only) batch flow.
 *
 *  Like ConfirmDialog, but when `reason` is set it also collects a reason /
 *  comment (recorded in the resource's change log or on the rows themselves).
 *  `'required'` blocks confirm until the text is non-empty; `'optional'`
 *  allows an empty field (then reported as undefined). The text is handed
 *  back via `onConfirm({ reason })`.
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useState } from 'react';

interface Props {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  destructive?: boolean;
  reason?: 'required' | 'optional';
  reasonLabel?: string;
  onConfirm: (params: { reason?: string }) => void;
  onCancel: () => void;
}

export function BatchConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  destructive = false,
  reason,
  reasonLabel,
  onConfirm,
  onCancel,
}: Props) {
  const [text, setText] = useState('');
  const trimmed = text.trim();
  const blocked = reason === 'required' && trimmed.length === 0;

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
        {reason && (
          <TextField
            label={reasonLabel ?? 'Reason'}
            required={reason === 'required'}
            fullWidth
            multiline
            minRows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Recorded in the change log"
            sx={{ mt: 2 }}
            autoFocus
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={() => onConfirm({ reason: reason ? trimmed || undefined : undefined })}
          color={destructive ? 'error' : 'primary'}
          variant="contained"
          disabled={blocked}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
